/**
 * Internal Blackbaud API Usage contracts.
 *
 *   GET /internal/blackbaud-usage/schools/:school_id
 *   GET /internal/blackbaud-usage/unattributed
 *
 * Super-admin reads over the platform's own usage record — not Datadog. The
 * record is the system of record precisely because Datadog's `school.slug` and
 * `school.id` tags degrade to `N/A` as the series ages while still returning
 * numbers, so per-school history read from there stops being trustworthy at
 * around 40 days without saying so.
 */

import { z } from 'zod'
import { BlackbaudUsageDaySchema } from '../../schemas/entities/blackbaud-usage'

/** Default and maximum length of the history window, in UTC days. */
export const BLACKBAUD_USAGE_DEFAULT_WINDOW_DAYS = 14
export const BLACKBAUD_USAGE_MAX_WINDOW_DAYS = 120

export const GetBlackbaudUsageQuerySchema = z.object({
  days: z.coerce
    .number()
    .int()
    .min(1)
    .max(BLACKBAUD_USAGE_MAX_WINDOW_DAYS)
    .optional()
    .describe('How many UTC days back to return, ending today. Defaults to 14.')
})
export type GetBlackbaudUsageQuery = z.infer<typeof GetBlackbaudUsageQuerySchema>

export const BlackbaudUsageResponseSchema = z.object({
  /** School public id, or `unattributed` for the fleet-wide no-school bucket (R22). */
  subject: z.string(),
  /** The quota the days are expressed against, or null for the unattributed bucket. */
  quota: z.number().int().positive().nullable(),
  /** The shared top-endpoint cutoff the days were built with, echoed so a client cannot assume a different one. */
  topEndpointCutoff: z.number().int().positive(),
  /**
   * Most recent day first. Days with no recorded usage are omitted rather than
   * zero-filled — an absent day and a day that genuinely saw no traffic are
   * different facts, and only the record can tell them apart.
   */
  days: z.array(BlackbaudUsageDaySchema)
})
export type BlackbaudUsageResponse = z.infer<typeof BlackbaudUsageResponseSchema>
